import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { API_BASE_URL } from '../../config';
import Logo from '../../assets/logo_jothi.jpg';

Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxP.ttf', fontWeight: 'normal' },
    { src: 'https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmEU9fBBc9.ttf', fontWeight: 'bold' },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontFamily: 'Roboto',
    fontSize: 8,
    backgroundColor: '#ffffff',
    color: '#333333',
  },
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    opacity: 0.08,
    fontSize: 40,
    color: '#aaaaaa',
    fontWeight: 'bold',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#004d99',
    paddingBottom: 6,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  logo: {
    width: 35,
    height: 35,
    marginRight: 6,
  },
  profileImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#004d99',
    marginBottom: 1,
  },
  subtitle: {
    fontSize: 9,
    color: '#666666',
  },
  currentDate: {
    fontSize: 7,
    color: '#666666',
    marginBottom: 1,
  },
  topSection: {
    flexDirection: 'row',
    marginBottom: 8,
    gap: 10,
  },
  leftSection: {
    flex: 1,
    paddingRight: 10,
  },
  rightSection: {
    width: 80,
    alignItems: 'center',
  },
  customerInfo: {
    marginBottom: 8,
    backgroundColor: '#f9f9f9',
    padding: 6,
    borderRadius: 3,
  },
  customerRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  customerLabel: {
    width: 70,
    fontWeight: 'bold',
    color: '#004d99',
    fontSize: 7,
  },
  customerValue: {
    fontSize: 7,
    flex: 1,
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#004d99',
    marginTop: 6,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#004d99',
    paddingBottom: 1,
  },
  loanSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  loanSummaryItem: {
    width: '32%',
  },
  loanSummaryLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#004d99',
  },
  loanSummaryValue: {
    fontSize: 7,
  },
  summaryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 4,
  },
  summaryItem: {
    width: '23%',
    padding: 4,
    backgroundColor: '#f0f5ff',
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#d9e6ff',
  },
  summaryItemLabel: {
    fontWeight: 'bold',
    marginBottom: 1,
    color: '#004d99',
    fontSize: 6,
  },
  summaryItemValue: {
    fontSize: 7,
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#004d99',
    borderRadius: 3,
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    minHeight: 14,
  },
  tableColHeader: {
    backgroundColor: '#004d99',
    color: '#ffffff',
    fontWeight: 'bold',
    padding: 2,
    textAlign: 'center',
    fontSize: 6,
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
    justifyContent: 'center',
  },
  tableCol: {
    padding: 2,
    fontSize: 6,
    borderRightWidth: 1,
    borderRightColor: '#e0e0e0',
    justifyContent: 'center',
  },
  tableColCenter: {
    textAlign: 'center',
  },
  tableColRight: {
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 25,
    left: 15,
    right: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#004d99',
    paddingTop: 6,
  },
  signature: {
    fontSize: 7,
    textAlign: 'center',
  },
  signatureLine: {
    width: 80,
    borderTopWidth: 1,
    borderTopColor: '#333333',
    marginBottom: 2,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 6,
    color: '#666666',
  },
});

// Utils
const formatCurrency = (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const [day, month, year] = dateString.split('.');
    if (!day || !month || !year) return 'Invalid Date';
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);
    if (
      isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum) ||
      dayNum < 1 || dayNum > 31 ||
      monthNum < 1 || monthNum > 12 ||
      yearNum < 1000 || yearNum > 9999
    ) {
      return 'Invalid Date';
    }
    const formattedDay = dayNum.toString().padStart(2, '0');
    const formattedMonth = monthNum.toString().padStart(2, '0');
    return `${formattedDay}.${formattedMonth}.${year}`;
  } catch {
    return 'Invalid Date';
  }
};

const getCurrentDateTime = () => {
  const now = new Date();
  return now.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const LoanStatementPDF = ({ customer, loanData, summaryData, filteredPaymentData }) => {
  const currentDateTime = getCurrentDateTime();
  // Updated column widths for 12 columns to fit properly
  const columnWidths = ['5%', '8%', '8%', '8%', '8%', '8%', '7%', '8%', '8%', '8%', '8%', '8%'];

  // Column headers matching the data structure
  const columnHeaders = [
    'No', 'Due Date', 'Pay Date', 'Principal', 'Interest', 'Total',
    'Status', 'Received', 'Outstanding', 'Arrear P', 'Arrear I', 'Arrear T'
  ];


  const maxRows = 24;
  const limitedPaymentData = filteredPaymentData.slice(0, maxRows);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.watermark}>JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</Text>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image style={styles.logo} src={Logo} />
            <View style={styles.headerText}>
              <Text style={styles.title}>JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</Text>
              <Text style={styles.subtitle}>Loan Statement</Text>
              <Text style={styles.currentDate}>Generated on: {currentDateTime}</Text>
            </View>
          </View>
        </View>

        {/* Top Section - Left: Loan Details, Right: Image */}
        <View style={styles.topSection}>
          {/* Left Section - Customer Info and Loan Details */}
          <View style={styles.leftSection}>
            {/* Customer Info - Compact */}
            <View style={styles.customerInfo}>
              <View style={styles.customerRow}>
                <Text style={styles.customerLabel}>Customer:</Text>
                <Text style={styles.customerValue}>{customer.name} (ID: {customer.customerUniqueId})</Text>
              </View>
              <View style={styles.customerRow}>
                <Text style={styles.customerLabel}>Mobile:</Text>
                <Text style={styles.customerValue}>{customer.mobileNumber}</Text>
              </View>
              <View style={styles.customerRow}>
                <Text style={styles.customerLabel}>Address:</Text>
                <Text style={styles.customerValue}>{customer.permanentStreetAddress || 'N/A'}</Text>
              </View>
            </View>

            {/* Loan Summary - Horizontal */}
            <Text style={styles.sectionTitle}>Loan Summary</Text>
            <View style={styles.loanSummaryRow}>
              <View style={styles.loanSummaryItem}>
                <Text style={styles.loanSummaryLabel}>Loan Amount:</Text>
                <Text style={styles.loanSummaryValue}>{formatCurrency(loanData?.totalLoanAmount)}</Text>
              </View>
              <View style={styles.loanSummaryItem}>
                <Text style={styles.loanSummaryLabel}>Loan Date:</Text>
                <Text style={styles.loanSummaryValue}>{formatDate(loanData?.emiDate)}</Text>
              </View>
              <View style={styles.loanSummaryItem}>
                <Text style={styles.loanSummaryLabel}>Tenure:</Text>
                <Text style={styles.loanSummaryValue}>{loanData?.tenureInMonths || 'N/A'} months</Text>
              </View>
            </View>

            {/* Financial Summary - Compact */}
            <Text style={styles.sectionTitle}>Financial Summary</Text>
            <View style={styles.summaryContainer}>
              {summaryData.map((item, idx) => (
                <View key={idx} style={styles.summaryItem}>
                  <Text style={styles.summaryItemLabel}>{item.label}</Text>
                  <Text style={styles.summaryItemValue}>{formatCurrency(item.value)}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* Right Section - Customer Image */}
          <View style={styles.rightSection}>
            {customer?.customerProfileImage && (
              <Image
                style={styles.profileImage}
                src={`${API_BASE_URL}/images/${customer.customerProfileImage}`}
              />
            )}
          </View>
        </View>

        {/* EMI Table - Compact */}
        <Text style={styles.sectionTitle}>EMI Payment Details</Text>
        <View style={styles.table}>
          {/* Header Row */}
          <View style={styles.tableRow}>
            {columnHeaders.map((text, i) => (
              <View key={i} style={[styles.tableColHeader, { width: columnWidths[i] }]}>
                <Text>{text}</Text>
              </View>
            ))}
          </View>

          {/* Data Rows */}
          {limitedPaymentData.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <View style={[styles.tableCol, { width: columnWidths[0] }, styles.tableColCenter]}>
                <Text>{item.dueNo}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[1] }, styles.tableColCenter]}>
                <Text>{formatDate(item.dueDate)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[2] }, styles.tableColCenter]}>
                <Text>{item.paymentDate === 'N/A' ? '-' : formatDate(item.paymentDate)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[3] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.principal)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[4] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.interest)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[5] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.total)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[6] }, styles.tableColCenter]}>
                <Text>{item.received}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[7] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.emiReceived)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[8] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.outstanding)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[9] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.arrearPrincipal)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[10] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.arrearInterest)}</Text>
              </View>
              <View style={[styles.tableCol, { width: columnWidths[11] }, styles.tableColRight]}>
                <Text>{formatCurrency(item.arrearTotal)}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Show total rows info */}
        {filteredPaymentData.length > maxRows && (
          <Text style={{ fontSize: 6, color: '#666666', marginBottom: 4, textAlign: 'center' }}>
            Showing first {maxRows} entries of {filteredPaymentData.length} total EMI records
          </Text>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.signature}>
            <View style={styles.signatureLine} />
            <Text>Customer Signature</Text>
          </View>
          <View style={styles.signature}>
            <View style={styles.signatureLine} />
            <Text>Authorized Signatory</Text>
          </View>
        </View>

        {/* Page Number */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default LoanStatementPDF;